import React, { useEffect, useState } from "react";
import { Chip, Grid, Stack, Typography } from "@mui/joy";
import {
  AutomaticThought,
  AutomaticThoughtFieldKeyType,
  AutomaticThoughtFieldMap,
  conceptApi,
  IndividualThoughtType,
} from "api/conceptApi";

import DeleteAutomaticThoughtButton from "components/coachRightSidebar/conceptNote/DeleteAutomaticThoughtButton";

import AutomaticThoughtInput from "./AutomaticThoughtInput";
import { useMutation } from "@tanstack/react-query";
import { useRecoilValue } from "recoil";
import { patientAtom } from "recoil/patientAtom";
import AutomaticThoughtConfirmButton from "./AutomaticThoughtConfirmButton";
import ResetAutomaticThoughtButton from "../ResetAutomaticThoughtButton";
import ReferenceLink from "../ReferenceLink";

interface AutomaticThoughtListItemProps {
  automaticThought: AutomaticThought;
  automaticThoughtList: AutomaticThought[];
  onDelete?: (automaticThoughtId: string) => void;
  highlight?: boolean;
}

const thoughtTrapPlaceholders: Record<string, string> = {
  "책임 과다": "X 때문에 Y 됐네",
  "근거 부족": "X인거야",
  "부정 편향": "X면 어쩌지? 결국 Y 될지 몰라",
  평가: "X는 Y해",
  반추: "X한 걸까? X면 어쩌지?",
  개념화: "난 원래 X한 사람이야",
};

export default function AutomaticThoughtListItem({
  automaticThought,
  automaticThoughtList,
  onDelete,
  highlight = false,
}: AutomaticThoughtListItemProps) {
  const [localData, setLocalData] =
    useState<AutomaticThought>(automaticThought);
  const [canBeConfirmed, setCanBeConfirmed] = useState<boolean>(false);
  const [selectedThoughtTraps, setSelectedThoughtTraps] = useState<string[]>(
    []
  );

  const patientState = useRecoilValue(patientAtom);
  const [inputDisable, setInputDisable] = useState<boolean>(false);

  useEffect(() => {
    setLocalData(automaticThought);
  }, [automaticThought]);

  useEffect(() => {
    setSelectedThoughtTraps(
      (localData.thoughtList || []).map((item) => item.thoughtTrap || "")
    );
  }, [localData.thoughtList]);

  useEffect(() => {
    const validSituation = !!localData.situation?.trim();
    // thoughtList 중 하나라도 thought, thoughtTrap 모두 채워진 게 있으면 만족
    const validThoughtList =
      (localData.thoughtList?.length ?? 0) >= 1 &&
      !!localData.thoughtList!![0]?.thought?.trim() &&
      !!localData.thoughtList!![0]?.thoughtTrap?.trim();

    const validEmotion = !!localData.emotion?.trim();
    const validPhysicalResponse = !!localData.physicalResponse?.trim();
    const validBehavior = !!localData.behavior?.trim();

    setCanBeConfirmed(
      validSituation &&
        validThoughtList &&
        validEmotion &&
        validPhysicalResponse &&
        validBehavior
    );

    if (localData.taskProgressStatus === "completed") {
      setInputDisable(true);
    }
  }, [localData]);

  const { mutate: updateAutomaticThought } = useMutation<
    void, // 성공 응답 타입
    unknown, // 에러 타입
    AutomaticThoughtFieldMap
  >(
    async (payload) => {
      await conceptApi.updateAutomaticThought(
        patientState?.patientId!!,
        automaticThought.automaticThoughtId,
        payload
      );
    },
    {
      onSuccess: (_data, variables) => {},
      onError: (error) => {
        console.error("Note update failed:", error);
      },
    }
  );

  const handleSaveSituation = (newValue: string) => {
    setLocalData((prev) => ({
      ...prev,
      situation: newValue,
    }));

    updateAutomaticThought({
      situation: newValue,
    });
  };

  function handleUpdateThoughtAndThoughtTrapList(
    index: number,
    field: keyof IndividualThoughtType,
    newValue: string
  ) {
    const newThoughtList = [...(localData.thoughtList ?? [])];

    while (newThoughtList.length <= index) {
      newThoughtList.push({ thought: "", thoughtTrap: "" });
    }

    newThoughtList[index] = {
      ...newThoughtList[index],
      [field]: newValue,
    };

    const updatedLocalData = {
      ...localData,
      thoughtList: newThoughtList,
    };
    setLocalData(updatedLocalData);

    const automaticThoughtFieldKey =
      field === "thought"
        ? (`thought${index + 1}` as AutomaticThoughtFieldKeyType)
        : (`thought${index + 1}ThoughtTrap` as AutomaticThoughtFieldKeyType);

    updateAutomaticThought({
      [automaticThoughtFieldKey]: newValue,
    });
  }

  const handleSaveEmotion = (newValue: string) => {
    setLocalData((prev) => ({
      ...prev,
      emotion: newValue,
    }));

    updateAutomaticThought({
      emotion: newValue,
    });
  };

  const handleSavePhysicalResponse = (newValue: string) => {
    setLocalData((prev) => ({
      ...prev,
      physicalResponse: newValue,
    }));

    updateAutomaticThought({
      physicalResponse: newValue,
    });
  };

  const handleSaveBehavior = (newValue: string) => {
    setLocalData((prev) => ({
      ...prev,
      behavior: newValue,
    }));

    updateAutomaticThought({
      behavior: newValue,
    });
  };

  const handleThoughtTrapChange = (index: number, newValue: string) => {
    setSelectedThoughtTraps((prev) => {
      const newSelected = [...prev];
      newSelected[index] = newValue;
      return newSelected;
    });
  };

  return (
    <Stack
      spacing={1}
      p={0.3}
      sx={{
        ...(highlight && {
          borderRadius: "4px",
          boxShadow: `0 0 0 1px rgba(15,85,167,0.3)`,
        }),
        backgroundColor: "unset",
        transition: "background-color 0.3s ease",
      }}
    >
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant={"Footnote"} sx={{ fontWeight: 500 }}>
            자동적 사고 {localData.sequence}
          </Typography>

          {(localData.automaticThoughtStatus === "note_confirmed" ||
            localData.automaticThoughtStatus === "confirmed") && (
            <>
              {localData.taskProgressStatus === "in_progress" ? (
                <Chip size="sm" color="success" variant="outlined">
                  진행 중
                </Chip>
              ) : localData.taskProgressStatus === "completed" ? (
                <Chip size="sm" color="neutral" variant="outlined">
                  완료
                </Chip>
              ) : (
                <Chip size="sm" color="warning" variant="outlined">
                  대기 중
                </Chip>
              )}
            </>
          )}
          <ReferenceLink
            reference={"기록 보기"}
            thoughtRecordKey={localData.thoughtRecordKey}
          />
        </Stack>

        <Stack direction="row" spacing={1}>
          {localData.automaticThoughtStatus !== "preparing" &&
            localData.taskProgressStatus !== "completed" && (
              <ResetAutomaticThoughtButton
                automaticThoughtId={localData.automaticThoughtId}
                thoughtRecordKey={localData.thoughtRecordKey}
              />
            )}
          {localData.automaticThoughtStatus === "preparing" && onDelete && (
            <DeleteAutomaticThoughtButton
              automaticThoughtId={localData.automaticThoughtId}
              thoughtRecordKey={localData.thoughtRecordKey}
            />
          )}
          {localData.automaticThoughtStatus === "preparing" && (
            <AutomaticThoughtConfirmButton
              automaticThoughtId={localData.automaticThoughtId}
              isOtherAutomaticThoughtAlreadyProgress={
                automaticThoughtList.filter(
                  (e) =>
                    e.taskProgressStatus === "in_progress" &&
                    !e.isForValue &&
                    e.automaticThoughtId !== localData.automaticThoughtId
                ).length >= 1
              }
              disabled={!canBeConfirmed}
            />
          )}
        </Stack>
      </Stack>

      {/* 상황 */}
      <Grid container>
        <Grid xs={2.5} sx={{ pt: "10px" }}>
          <Typography variant={"Footnote"} sx={{ fontWeight: 500, pl: 0.8 }}>
            상황
          </Typography>
        </Grid>
        <Grid xs={9.5}>
          <AutomaticThoughtInput
            type="textarea"
            initialValue={localData.situation ?? ""}
            onSave={handleSaveSituation}
            disable={inputDisable}
          />
        </Grid>
      </Grid>

      {/* 생각(thoughtList) */}
      {(localData.thoughtList || []).map((item, idx) => (
        <Stack spacing={1} key={idx}>
          {/* 생각 함정(thoughtTrap) */}
          <Grid container>
            <Grid xs={2.5} sx={{ pt: "10px" }}>
              {idx === 0 && (
                <Typography
                  variant={"Footnote"}
                  sx={{ fontWeight: 500, pl: 0.8 }}
                >
                  생각
                </Typography>
              )}
            </Grid>

            <Grid xs={9.5}>
              <AutomaticThoughtInput
                type="select"
                initialValue={item.thoughtTrap ?? ""}
                onChange={(newValue) => handleThoughtTrapChange(idx, newValue)}
                onSave={(newValue) =>
                  handleUpdateThoughtAndThoughtTrapList(
                    idx,
                    "thoughtTrap",
                    newValue
                  )
                }
                disable={inputDisable}
              />
            </Grid>
          </Grid>

          {/* 생각 */}
          <Grid container>
            <Grid xs={2.5} sx={{ pt: "10px" }}></Grid>
            <Grid xs={9.5}>
              <AutomaticThoughtInput
                type="textarea"
                initialValue={item.thought ?? ""}
                placeholder={
                  thoughtTrapPlaceholders[selectedThoughtTraps[idx]] || ""
                }
                onSave={(newValue) =>
                  handleUpdateThoughtAndThoughtTrapList(
                    idx,
                    "thought",
                    newValue
                  )
                }
                disable={inputDisable}
              />
            </Grid>
          </Grid>
        </Stack>
      ))}

      {/* 감정 */}
      <Grid container>
        <Grid xs={2.5} sx={{ pt: "10px" }}>
          <Typography variant={"Footnote"} sx={{ fontWeight: 500, pl: 0.8 }}>
            감정
          </Typography>
        </Grid>
        <Grid xs={9.5}>
          <AutomaticThoughtInput
            type="textarea"
            initialValue={localData.emotion ?? ""}
            onSave={handleSaveEmotion}
            disable={inputDisable}
          />
        </Grid>
      </Grid>

      {/* 신체 반응 */}
      <Grid container>
        <Grid xs={2.5} sx={{ pt: "10px" }}>
          <Typography variant={"Footnote"} sx={{ fontWeight: 500, pl: 0.8 }}>
            신체 반응
          </Typography>
        </Grid>
        <Grid xs={9.5}>
          <AutomaticThoughtInput
            type="textarea"
            initialValue={localData.physicalResponse ?? ""}
            onSave={handleSavePhysicalResponse}
            disable={inputDisable}
          />
        </Grid>
      </Grid>

      {/* 행동 */}
      <Grid container>
        <Grid xs={2.5} sx={{ pt: "10px" }}>
          <Typography variant={"Footnote"} sx={{ fontWeight: 500, pl: 0.8 }}>
            행동
          </Typography>
        </Grid>
        <Grid xs={9.5}>
          <AutomaticThoughtInput
            type="textarea"
            initialValue={localData.behavior ?? ""}
            onSave={handleSaveBehavior}
            disable={inputDisable}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
